import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { remoteLabel } from "@/lib/marketplace";
import { ClipboardText, Plus, MapPin, ChatCircleDots, Trash, PaperPlaneTilt, X } from "@phosphor-icons/react";

const inputCls = "bg-surface-2 border-border text-fg";
const empty = { title: "", description: "", role: "", family: "", skills: "", seniority: "", city: "", remote_preference: "UNKNOWN", mission_type: "" };

export default function MarketplaceRequests() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [contactReq, setContactReq] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/marketplace/requests?mine=${mine ? "true" : "false"}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [mine]);
  useEffect(() => { load(); }, [load]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    if (!form.title.trim()) { toast.error("Le titre est requis."); return; }
    setBusy(true);
    try {
      await api.post("/marketplace/requests", form);
      toast.success("Demande publiée");
      setOpen(false); setForm(empty); setMine(true); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Publication impossible"); }
    finally { setBusy(false); }
  };

  const closeReq = async (r) => {
    try { await api.patch(`/marketplace/requests/${r.id}`, { status: r.status === "OPEN" ? "CLOSED" : "OPEN" }); load(); }
    catch { toast.error("Action impossible"); }
  };
  const del = async (r) => {
    try { await api.delete(`/marketplace/requests/${r.id}`); setItems((x) => x.filter((y) => y.id !== r.id)); toast.success("Demande supprimée"); }
    catch { toast.error("Suppression impossible"); }
  };

  const contact = async () => {
    if (!msg.trim()) { toast.error("Écrivez un message."); return; }
    setBusy(true);
    try {
      await api.post("/marketplace/conversations", { request_id: contactReq.id, message: msg.trim() });
      toast.success("Message envoyé");
      setContactReq(null); setMsg("");
      navigate("/app/marketplace/messages");
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="marketplace-requests">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><ClipboardText size={14} className="text-accent" /> Demandes de talents</div>
          <h1 className="font-display text-3xl font-bold">Besoins en consultants</h1>
          <p className="text-muted text-sm mt-1">Publiez un besoin ou répondez à ceux des autres organisations via la messagerie interne.</p>
        </div>
        <button onClick={() => { setForm(empty); setOpen(true); }} data-testid="mk-new-request-btn"
          className="bg-accent text-black font-semibold px-4 py-2.5 rounded-md hover-lift flex items-center gap-2 shrink-0"><Plus size={16} weight="bold" /> Publier une demande</button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setMine(false)} data-testid="mk-req-all" className={`text-sm px-3 py-1.5 rounded-full border ${!mine ? "border-accent text-accent bg-accent/10" : "border-border text-muted"}`}>Toutes les demandes</button>
        <button onClick={() => setMine(true)} data-testid="mk-req-mine" className={`text-sm px-3 py-1.5 rounded-full border ${mine ? "border-accent text-accent bg-accent/10" : "border-border text-muted"}`}>Mes demandes</button>
      </div>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="surface rounded-lg p-12 text-center" data-testid="mk-req-empty">
          <ClipboardText size={40} weight="duotone" className="text-accent mx-auto mb-3" />
          <div className="font-display font-bold text-fg mb-1">{mine ? "Vous n'avez pas encore publié de demande" : "Aucune demande ouverte"}</div>
          <p className="text-muted text-sm">Publiez un besoin pour recevoir des propositions de consultants.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="mk-req-grid">
          {items.map((r) => (
            <div key={r.id} className="surface rounded-lg p-5" data-testid={`mk-req-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-bold text-fg">{r.title}</h3>
                {r.status === "CLOSED" && <span className="text-[11px] font-mono px-2 py-0.5 rounded-full border border-border text-muted">Clôturée</span>}
              </div>
              <div className="text-xs text-muted mt-0.5">{r.org_name} · {r.org_type === "FINAL_CLIENT" ? "Client final" : "ESN"}</div>
              {r.description && <p className="text-sm text-fg/80 mt-2 line-clamp-3 whitespace-pre-line">{r.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(r.skills || []).slice(0, 6).map((s) => <span key={s} className="text-xs font-mono bg-surface-2 text-fg/80 border border-border px-2 py-0.5 rounded">{s}</span>)}
              </div>
              <div className="flex items-center gap-3 text-xs text-muted mt-3 flex-wrap">
                {r.seniority && <span>{r.seniority}</span>}
                {r.city && <span className="flex items-center gap-1"><MapPin size={12} /> {r.city}</span>}
                <span>{remoteLabel(r.remote_preference)}</span>
                {r.mission_type && <span className="font-mono">{r.mission_type}</span>}
              </div>
              <div className="mt-4 pt-3 border-t border-border flex items-center gap-2">
                {r.is_mine ? (
                  <>
                    <button onClick={() => closeReq(r)} data-testid={`mk-req-toggle-${r.id}`} className="text-xs text-muted hover:text-fg border border-border rounded-md px-2.5 py-1">{r.status === "OPEN" ? "Clôturer" : "Rouvrir"}</button>
                    <button onClick={() => del(r)} data-testid={`mk-req-del-${r.id}`} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 ml-auto"><Trash size={13} /> Supprimer</button>
                  </>
                ) : (
                  <button onClick={() => { setContactReq(r); setMsg(""); }} data-testid={`mk-req-contact-${r.id}`}
                    className="text-xs bg-accent/10 border border-accent/40 text-accent rounded-md px-3 py-1.5 flex items-center gap-1 ml-auto hover-lift"><ChatCircleDots size={13} /> Répondre</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Créer une demande */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="mk-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Publier une demande de talent</DialogTitle>
            <DialogDescription className="text-muted">Décrivez le profil recherché. Les ESN pourront vous proposer des consultants.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div><label className="overline block mb-1.5">Titre *</label><Input className={inputCls} value={form.title} onChange={upd("title")} placeholder="Recherche Data Engineer Spark" data-testid="mk-req-title" /></div>
            <div><label className="overline block mb-1.5">Description</label><textarea className={`${inputCls} w-full rounded-md text-sm px-3 py-2 min-h-[80px] resize-y`} value={form.description} onChange={upd("description")} placeholder="Contexte, durée, démarrage, TJM cible…" data-testid="mk-req-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="overline block mb-1.5">Métier</label><Input className={inputCls} value={form.role} onChange={upd("role")} placeholder="Data Engineer" data-testid="mk-req-role" /></div>
              <div><label className="overline block mb-1.5">Séniorité</label>
                <Select value={form.seniority || undefined} onValueChange={(v) => setForm({ ...form, seniority: v })}>
                  <SelectTrigger className={inputCls} data-testid="mk-req-seniority"><SelectValue placeholder="Séniorité" /></SelectTrigger>
                  <SelectContent>{["Junior", "Confirmé", "Senior", "Expert"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="overline block mb-1.5">Compétences (séparées par des virgules)</label><Input className={inputCls} value={form.skills} onChange={upd("skills")} placeholder="Spark, Python, Airflow" data-testid="mk-req-skills" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="overline block mb-1.5">Ville</label><Input className={inputCls} value={form.city} onChange={upd("city")} placeholder="Paris" data-testid="mk-req-city" /></div>
              <div><label className="overline block mb-1.5">Mode de travail</label>
                <Select value={form.remote_preference} onValueChange={(v) => setForm({ ...form, remote_preference: v })}>
                  <SelectTrigger className={inputCls} data-testid="mk-req-remote"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ONSITE">Sur site</SelectItem><SelectItem value="HYBRID">Hybride</SelectItem><SelectItem value="REMOTE">Télétravail</SelectItem><SelectItem value="UNKNOWN">Non précisé</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="overline block mb-1.5">Type de mission</label><Input className={inputCls} value={form.mission_type} onChange={upd("mission_type")} placeholder="Freelance / Régie / Forfait" data-testid="mk-req-mission" /></div>
          </div>
          <DialogFooter>
            <button onClick={create} disabled={busy} data-testid="mk-req-submit" className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift w-full disabled:opacity-60">Publier la demande</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Répondre à une demande */}
      <Dialog open={!!contactReq} onOpenChange={(o) => !o && setContactReq(null)}>
        <DialogContent className="bg-surface border-border max-w-md" data-testid="mk-req-contact-dialog">
          {contactReq && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Répondre — {contactReq.title}</DialogTitle>
                <DialogDescription className="text-muted">{contactReq.org_name} · via la messagerie interne</DialogDescription>
              </DialogHeader>
              <textarea value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="mk-req-contact-message"
                placeholder="Présentez le profil disponible que vous proposez…"
                className={`${inputCls} w-full rounded-md text-sm px-3 py-2 min-h-[100px] resize-y`} />
              <DialogFooter>
                <button onClick={contact} disabled={busy} data-testid="mk-req-contact-send" className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift w-full flex items-center justify-center gap-2 disabled:opacity-60"><PaperPlaneTilt size={16} weight="fill" /> Envoyer</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
